import type { ButtonHTMLAttributes, ReactNode } from 'react';

import { cn } from '@/shared/lib/cn';

type Variant = 'primary' | 'secondary' | 'ghost' | 'danger' | 'success' | 'warning';
type Size = 'sm' | 'md' | 'lg';

const VARIANTS: Record<Variant, string> = {
  // The brand is black, so hover goes *lighter*. Darkening a near-black
  // surface produces no perceptible change and the button feels dead.
  primary: 'bg-brand-900 text-white hover:bg-brand-700 disabled:bg-brand-400',
  secondary: 'bg-surface text-ink border border-border-strong hover:bg-surface-sunken',
  ghost: 'text-ink hover:bg-surface-sunken',
  danger: 'bg-danger text-white hover:opacity-90',
  // Matches the "Activo" badge tone: soft green, for constructive actions
  // like reactivating a record — never for destructive ones.
  success: 'bg-success-soft text-success hover:bg-success/15',
  // Solid amber, mirroring the danger pattern: a resume CTA for an unfinished
  // attempt is the one action the worker must notice before anything else.
  warning: 'bg-warning text-white hover:opacity-90',
};

const SIZES: Record<Size, string> = {
  // 44px minimum touch target on the smallest size: the manual requires
  // frequent flows to work from a phone (RNF-01).
  sm: 'h-9 px-3 text-sm',
  md: 'h-11 px-4 text-sm',
  lg: 'h-12 px-6 text-base',
};

interface Props extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  size?: Size;
  isLoading?: boolean;
  icon?: ReactNode;
}

export function Button({
  variant = 'primary',
  size = 'md',
  isLoading = false,
  icon,
  className,
  children,
  disabled,
  type = 'button',
  ...rest
}: Props) {
  return (
    <button
      type={type}
      disabled={disabled ?? isLoading}
      aria-busy={isLoading}
      className={cn(
        'inline-flex cursor-pointer items-center justify-center gap-2 rounded-(--radius-control) font-medium',
        'transition-colors',
        'focus:outline-none focus:ring-2 focus:ring-brand-700 focus:ring-offset-2',
        'disabled:cursor-not-allowed disabled:opacity-70',
        VARIANTS[variant],
        SIZES[size],
        className,
      )}
      {...rest}
    >
      {isLoading ? (
        <span className="size-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
      ) : (
        icon
      )}
      {children}
    </button>
  );
}
